import { useRouter } from 'vue-router'
import { triggerBrowserMessage } from '~/app.vue'
import { firebaseUser, initUser, isUserLoaded, signOutUser } from '~/composables/useFirebase'
import { hasPendingEmailMfaChallenge, syncMfaEnrollmentIfNeededSafe } from '~/composables/useMfa'
import { NuxtRoutes } from '~/constants/routes'
import {
  getSessionStorageItem,
  putSessionStorageItem,
  removeSessionStorageItem,
} from '~/composables/useUtils'
import {
  canonicalRouteName,
  canonicalizeHomePath,
  isAlwaysPermittedRoute,
  isPublicAuthRoute,
} from '~/utils/publicAuthRoutes'

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()

  if (import.meta.client) {
    window.addEventListener('beforeunload', () => {
      removeSessionStorageItem('lastValidRoute')
    })
  }

  router.beforeResolve(async (to) => {
    if (!import.meta.client) {
      return true
    }

    const canonicalHome = canonicalizeHomePath(to.path)
    if (canonicalHome) {
      return {
        path: canonicalHome,
        query: to.query,
        hash: to.hash,
        replace: true,
      }
    }

    try {
      if (!isUserLoaded.value) {
        await initUser()
      }

      const routeName = canonicalRouteName(to.name)
      const lastValidRoute = getSessionStorageItem('lastValidRoute')
      const isLoggedIn = Boolean(firebaseUser.value && isUserLoaded.value)
      const publicAuthRoute = isPublicAuthRoute(to)
      const i18n = nuxtApp.$i18n as { t: (key: string) => string }

      if (isAlwaysPermittedRoute(to)) {
        return true
      }

      if (!isLoggedIn) {
        if (publicAuthRoute) {
          return true
        }

        triggerBrowserMessage(
          'Session abgelaufen. Bitte melden Sie sich erneut an.',
        )
        return router.push({ name: NuxtRoutes.AuthLogin })
      }
      if (routeName === NuxtRoutes.AuthMfaSetup) {
        return true
      }

      const mfaResult = await syncMfaEnrollmentIfNeededSafe()
      if (!mfaResult.ok) {
        triggerBrowserMessage(i18n.t('auth.mfa.statusCheckFailed'))
      }
      else if (mfaResult.status?.enrollmentRequired) {
        triggerBrowserMessage(i18n.t('auth.mfa.enrollmentRequiredRedirect'))
        return router.push({ name: NuxtRoutes.AuthMfaSetup })
      }

      else if (mfaResult.status?.verificationRequired) {
        if (hasPendingEmailMfaChallenge()) {
          if (routeName === NuxtRoutes.AuthMfaChallenge) return true
          return router.push({ name: NuxtRoutes.AuthMfaChallenge })
        }
        await signOutUser()
        triggerBrowserMessage(i18n.t('auth.mfa.verificationRequiredLoginAgain'))
        return router.push({ name: NuxtRoutes.AuthLogin })
      }
      if (publicAuthRoute) {
        triggerBrowserMessage(
          'Sie sind bereits eingeloggt. Sie müssen sich erst abmelden',
        )
        return router.push({ name: lastValidRoute || NuxtRoutes.Home })
      }

      if (routeName) {
        putSessionStorageItem('lastValidRoute', routeName)
      }
      return true
    }
    catch (error) {
      $reportError('Router guard failed', error)
      triggerBrowserMessage('Routing Fehler')
      return false
    }
  })
})
